import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectSeparator, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, ArrowLeft, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { QUESTION_CATEGORIES, CUSTOM_OPTION } from '@/data/verificationQuestions';
import { DIVINATION_OPTIONS } from '@/data/divination';
import { SCOPE_OPTIONS } from '@/data/scopes';
import CityPicker from '@/components/CityPicker';
import {
  lunarToSolar, solarToLunarText, leapMonthOf, ganzhiAnimalOf,
  LUNAR_MONTHS, LUNAR_DAYS,
  SHICHEN, MIN_BIRTH_DATE, todayISO,
} from '@/data/birth';
import { hasContactInfo } from '@/data/contact';

const newQuestion = (key, order) => ({ _key: key, selected: '', customText: '', truth: '', order });

// The actual text submitted for a slot: a preset, or the typed custom text.
const finalText = (q) => (q.selected === CUSTOM_OPTION ? q.customText.trim() : q.selected);

const CreateRequest = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    client_name: '',
    calendar: 'solar',     // 'solar' | 'lunar'
    birth_date: '',        // solar date input
    lunar_year: '',
    lunar_month: '',
    lunar_day: '',
    lunar_leap: false,
    time_mode: 'exact',    // 'exact' | 'shichen' | 'unknown'
    birth_time: '',        // exact time input
    shichen: '',
    gender: '',
    region: '',
  });
  const [divinationTypes, setDivinationTypes] = useState(['any']);
  const [scopes, setScopes] = useState([]);
  const [questions, setQuestions] = useState([
    newQuestion('q1', 1),
    newQuestion('q2', 2),
  ]);
  const [nextKeyId, setNextKeyId] = useState(3);
  const [loading, setLoading] = useState(false);
  const [draftOwner, setDraftOwner] = useState(null);

  // Draft persistence: an accidental back/refresh on this long form no longer wipes it.
  const DRAFT_KEY = `fm_request_draft:${user.user_id}`;
  useEffect(() => {
    try {
      localStorage.removeItem('fm_request_draft'); // discard legacy shared-account data
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d.savedAt || Date.now() - d.savedAt > 7 * 86400000) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      if (d.formData) setFormData(d.formData);
      if (d.divinationTypes) setDivinationTypes(d.divinationTypes);
      if (d.scopes) setScopes(d.scopes);
      if (d.questions && d.questions.length) setQuestions(d.questions);
      if (d.nextKeyId) setNextKeyId(d.nextKeyId);
      toast.info('已還原上次未提交嘅草稿');
    } catch (e) { /* corrupt draft — ignore */ }
    finally { setDraftOwner(user.user_id); }
  }, [DRAFT_KEY, user.user_id]);
  useEffect(() => {
    if (draftOwner !== user.user_id) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, divinationTypes, scopes, questions, nextKeyId, savedAt: Date.now() }));
    } catch (e) { /* storage full — ignore */ }
  }, [formData, divinationTypes, scopes, questions, nextKeyId, DRAFT_KEY, draftOwner, user.user_id]);

  const maxDate = todayISO();
  const lunarSolarPreview = (formData.calendar === 'lunar' && formData.lunar_year && formData.lunar_month && formData.lunar_day)
    ? lunarToSolar(formData.lunar_year, formData.lunar_month, formData.lunar_day, formData.lunar_leap)
    : null;
  const solarLunarPreview = (formData.calendar === 'solar' && formData.birth_date)
    ? solarToLunarText(formData.birth_date)
    : '';

  // 「不限」is exclusive; choosing a specific method clears it, and vice versa.
  const handleToggleType = (value) => {
    if (value === 'any') {
      setDivinationTypes(['any']);
      return;
    }
    setDivinationTypes((prev) => {
      const without = prev.filter((t) => t !== 'any' && t !== value);
      const next = prev.includes(value) ? without : [...without, value];
      return next.length ? next : ['any'];
    });
  };

  const handleAddQuestion = () => {
    if (questions.length < 3) {
      setQuestions([...questions, newQuestion(`q${nextKeyId}`, questions.length + 1)]);
      setNextKeyId(nextKeyId + 1);
    }
  };

  const handleRemoveQuestion = (index) => {
    if (questions.length > 2) {
      const newQuestions = questions.filter((_, i) => i !== index);
      setQuestions(newQuestions.map((q, i) => ({ ...q, order: i + 1 })));
    }
  };

  const handleSelect = (index, value) => {
    const next = [...questions];
    next[index] = { ...next[index], selected: value };
    setQuestions(next);
  };

  const handleCustom = (index, value) => {
    const next = [...questions];
    next[index] = { ...next[index], customText: value };
    setQuestions(next);
  };

  const handleTruth = (index, value) => {
    const next = [...questions];
    next[index] = { ...next[index], truth: value };
    setQuestions(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ---- Birth date: solar canonical + a human display label ----
    let birthDate = '';
    let birthDateDisplay = '';
    if (formData.calendar === 'lunar') {
      const { lunar_year, lunar_month, lunar_day, lunar_leap } = formData;
      if (!lunar_year || !lunar_month || !lunar_day) {
        toast.error('請完整填寫農曆出生日期');
        return;
      }
      birthDate = lunarToSolar(lunar_year, lunar_month, lunar_day, lunar_leap);
      if (!birthDate) {
        toast.error('農曆日期無效，請檢查年／月／日');
        return;
      }
      birthDateDisplay = `農曆 ${lunar_year}年${lunar_leap ? '閏' : ''}${lunar_month}月${lunar_day}日（西曆 ${birthDate}）`;
    } else {
      if (!formData.birth_date) {
        toast.error('請選擇出生日期');
        return;
      }
      birthDate = formData.birth_date;
      birthDateDisplay = `西曆 ${birthDate}`;
    }

    if (birthDate < MIN_BIRTH_DATE || birthDate > maxDate) {
      toast.error('出生日期須喺 1950 年至今日之間');
      return;
    }

    // ---- Birth time: canonical HH:MM (or empty) + display label ----
    let birthTime = '';
    let birthTimeDisplay = '';
    if (formData.time_mode === 'exact') {
      if (!formData.birth_time) {
        toast.error('請填寫出生時間，或改選時辰／不確定');
        return;
      }
      birthTime = formData.birth_time;
      birthTimeDisplay = formData.birth_time;
    } else if (formData.time_mode === 'shichen') {
      const sc = SHICHEN.find((s) => s.value === formData.shichen);
      if (!sc) {
        toast.error('請選擇時辰');
        return;
      }
      birthTime = sc.time;
      birthTimeDisplay = sc.label;
    } else {
      birthTime = '';
      birthTimeDisplay = '不確定（請命理師校時）';
    }

    if (!formData.gender) {
      toast.error('請選擇性別');
      return;
    }
    if (!formData.region.trim()) {
      toast.error('請選擇或輸入出生地區');
      return;
    }
    if (!divinationTypes.length) {
      toast.error('請選擇命理類型');
      return;
    }
    if (!scopes.length) {
      toast.error('請選擇批算範圍（流年／大批）');
      return;
    }

    const texts = questions.map(finalText);
    if (texts.some((t) => !t)) {
      toast.error('請為每條問題選擇一個選項或填寫自訂問題');
      return;
    }
    if (new Set(texts).size !== texts.length) {
      toast.error('問題不可重複，請選擇不同的問題');
      return;
    }
    if (texts.some((t) => hasContactInfo(t))) {
      toast.error('問題不可包含聯絡方式（電話、Email、社交帳號等），請移除後再提交');
      return;
    }

    // Truths are all-or-nothing: fill every question, or leave them all blank.
    const filledTruths = questions.filter((q) => (q.truth || '').trim());
    if (filledTruths.length > 0 && filledTruths.length < questions.length) {
      toast.error('真相要全部問題都填，或全部留空');
      return;
    }

    setLoading(true);
    try {
      const verification_questions = texts.map((question_text, i) => ({
        question_text,
        order: i + 1,
      }));
      // Optional private truths (the querent's own known answers) — used to score answers later.
      const verification_truths = {};
      questions.forEach((q, i) => {
        const t = (q.truth || '').trim();
        if (t) verification_truths[i + 1] = t;
      });
      const res = await axios.post(
        `${API}/requests`,
        {
          client_name: formData.client_name,
          birth_date: birthDate,
          birth_time: birthTime,
          calendar: formData.calendar,
          birth_date_display: birthDateDisplay,
          birth_time_display: birthTimeDisplay,
          gender: formData.gender,
          region: formData.region,
          divination_types: divinationTypes,
          scopes,
          verification_questions,
          verification_truths,
        },
        { withCredentials: true }
      );

      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
      toast.success('求測單已提交');
      navigate(`/client/request/${res.data.request_id}`);
    } catch (error) {
      console.error('Error creating request:', error);
      toast.error(error.response?.data?.detail || '提交失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <Link to="/client/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/client/dashboard">
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
            建立求測單
          </h1>
          <p className="text-base text-[#5C5C5C] mb-8">
            請填寫你的出生資料與驗證問題
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8">
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-6">基本資料</h3>

              <div className="mb-6">
                <Label htmlFor="client_name">姓名 *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="例：張小明"
                  className="rounded-sm mt-2"
                  required
                  data-testid="client-name-input"
                />
                <p className="text-xs text-[#5C5C5C] mt-1">
                  被占算者的姓名（可以是你自己或代為查問的對象）；命理師看不到真實姓名，只顯示為匿名
                </p>
              </div>

              {/* 出生日期（西曆／農曆） */}
              <div className="mb-6">
                <Label>出生日期 *</Label>
                <div className="flex gap-2 mt-2 mb-3">
                  {[{ v: 'solar', l: '西曆（陽曆）' }, { v: 'lunar', l: '農曆（陰曆）' }].map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      onClick={() => setFormData({ ...formData, calendar: c.v })}
                      className={`px-4 py-2 rounded-sm border text-sm transition-colors ${
                        formData.calendar === c.v
                          ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                      }`}
                      data-testid={`calendar-${c.v}`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>

                {formData.calendar === 'solar' ? (
                  <>
                    <Input
                      id="birth_date"
                      type="date"
                      min={MIN_BIRTH_DATE}
                      max={maxDate}
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      className="rounded-sm"
                      data-testid="birth-date-input"
                    />
                    {solarLunarPreview && (
                      <p className="text-xs text-[#C93B2B] mt-1">≈ {solarLunarPreview}</p>
                    )}
                  </>
                ) : (
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Input
                        type="number"
                        min="1950"
                        max={maxDate.slice(0, 4)}
                        value={formData.lunar_year}
                        onChange={(e) => setFormData({ ...formData, lunar_year: e.target.value, lunar_leap: false })}
                        placeholder="農曆年份"
                        className="rounded-sm"
                        data-testid="lunar-year"
                      />
                      <Select value={formData.lunar_month ? String(formData.lunar_month) : ''} onValueChange={(v) => setFormData({ ...formData, lunar_month: Number(v), lunar_leap: false })}>
                        <SelectTrigger className="rounded-sm" data-testid="lunar-month"><SelectValue placeholder="月" /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => <SelectItem key={i} value={String(i + 1)}>{LUNAR_MONTHS[i]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={formData.lunar_day ? String(formData.lunar_day) : ''} onValueChange={(v) => setFormData({ ...formData, lunar_day: Number(v) })}>
                        <SelectTrigger className="rounded-sm" data-testid="lunar-day"><SelectValue placeholder="日" /></SelectTrigger>
                        <SelectContent className="max-h-[40vh]">
                          {Array.from({ length: 30 }, (_, i) => <SelectItem key={i} value={String(i + 1)}>{LUNAR_DAYS[i]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {Number(formData.lunar_month) > 0 && leapMonthOf(formData.lunar_year) === Number(formData.lunar_month) && (
                        <label className="flex items-center justify-center gap-2 text-sm border border-[#E5E0D8] rounded-sm px-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.lunar_leap}
                            onChange={(e) => setFormData({ ...formData, lunar_leap: e.target.checked })}
                            data-testid="lunar-leap"
                          />
                          閏月
                        </label>
                      )}
                    </div>
                    {lunarSolarPreview ? (
                      <>
                        <p className="text-xs text-[#C93B2B] mt-1">
                          ≈ 西曆 {lunarSolarPreview}{ganzhiAnimalOf(lunarSolarPreview) ? `（${ganzhiAnimalOf(lunarSolarPreview)}）` : ''}
                        </p>
                        <p className="text-xs text-[#9A8A6A] mt-0.5">請核對生肖啱唔啱——農曆十一月／臘月出生，農曆年份可能比西曆（身份證）年份小 1 年</p>
                      </>
                    ) : (
                      <p className="text-xs text-[#5C5C5C] mt-1">農曆會自動換算成西曆畀命理師。年尾（十一月／臘月）出生請留意：農曆年份可能比身份證年份小 1 年</p>
                    )}
                  </div>
                )}
              </div>

              {/* 出生時間（準確／時辰／不確定） */}
              <div className="mb-6">
                <Label>出生時間 *</Label>
                <div className="flex flex-wrap gap-2 mt-2 mb-3">
                  {[{ v: 'exact', l: '準確時間' }, { v: 'shichen', l: '只知時辰' }, { v: 'unknown', l: '不確定' }].map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => setFormData({ ...formData, time_mode: t.v })}
                      className={`px-4 py-2 rounded-sm border text-sm transition-colors ${
                        formData.time_mode === t.v
                          ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                      }`}
                      data-testid={`time-mode-${t.v}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
                {formData.time_mode === 'exact' && (
                  <Input
                    id="birth_time"
                    type="time"
                    value={formData.birth_time}
                    onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                    className="rounded-sm"
                    data-testid="birth-time-input"
                  />
                )}
                {formData.time_mode === 'shichen' && (
                  <Select value={formData.shichen} onValueChange={(v) => setFormData({ ...formData, shichen: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="shichen-select"><SelectValue placeholder="選擇時辰" /></SelectTrigger>
                    <SelectContent className="max-h-[50vh]">
                      {SHICHEN.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {formData.time_mode === 'unknown' && (
                  <p className="text-sm text-[#5C5C5C] bg-[#F5F0E6] rounded-sm p-3">
                    命理師會在諮詢時為你校正時辰（不確定鐘數亦可求測）。
                  </p>
                )}
                {formData.time_mode !== 'unknown' && (
                  <p className="text-xs text-[#5C5C5C] mt-2">
                    請填寫出世紙／鐘錶時間（毋須自行調整）；命理師會按出生地校正真太陽時。
                  </p>
                )}
              </div>

              {/* 性別 + 地區 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="gender">性別 *</Label>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                    <SelectTrigger className="rounded-sm mt-2" data-testid="gender-select">
                      <SelectValue placeholder="選擇性別" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">男</SelectItem>
                      <SelectItem value="female">女</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>出生地區 *</Label>
                  <div className="mt-2">
                    <CityPicker
                      value={formData.region}
                      onChange={(v) => setFormData({ ...formData, region: v })}
                      testId="region-input"
                    />
                  </div>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    請填<b>出生</b>嘅城市（排盤按出生地校正）——唔係而家居住地。可搜尋主要城市，或選擇「其他」自行輸入
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <Label>命理類型 *（可多選）</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DIVINATION_OPTIONS.map((opt) => {
                    const active = divinationTypes.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleToggleType(opt.value)}
                        className={`px-4 py-2 rounded-sm border text-sm transition-colors ${
                          active
                            ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                            : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                        }`}
                        data-testid={`divination-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#5C5C5C] mt-2">
                  「不限」代表你接受任何命理方法；選擇具體類型則代表只希望使用該幾種。
                </p>
              </div>

              <div className="mt-6">
                <Label>批算範圍 *（可多選）</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SCOPE_OPTIONS.map((s) => {
                    const active = scopes.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setScopes((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))}
                        className={`px-4 py-2 rounded-sm border text-sm transition-colors ${
                          active
                            ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                            : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                        }`}
                        data-testid={`scope-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#5C5C5C] mt-2">
                  命理師會就你揀嘅每個範圍分別報價同時間（流年＝該年運勢；大批＝整體大運）。
                </p>
              </div>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8">
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">驗證問題</h3>
              <p className="text-sm text-[#5C5C5C] mb-4">
                請選擇 2-3 條問題，讓命理師根據你的命盤回答，再由你驗證是否準確。
              </p>

              <div className="bg-[#F5F0E6] rounded-sm p-4 mb-4 space-y-1">
                <p className="text-sm text-[#1A1A1A]">
                  <span className="text-[#2F7D4F] font-medium">✅ 只詢問「現在」或「過去」</span> —— 你已知道答案，方能驗證準確度
                </p>
                <p className="text-sm text-[#1A1A1A]">
                  <span className="text-[#C93B2B] font-medium">❌ 請勿詢問「未來」</span> —— 預測無法即時驗證
                </p>
                <p className="text-xs text-[#5C5C5C] pt-1">
                  你可以選擇概括題（如「我的家庭」讓命理師自由發揮），或具體題（測試命理師的論盤功力），亦可自訂。
                </p>
              </div>

              <div className="bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-4 mb-6">
                <p className="text-sm text-[#1A1A1A] font-medium">
                  ⚠️ 請勿在問題中分享個人聯絡資訊（電話、Email、社群帳號等）
                </p>
              </div>

              <div className="space-y-5">
                {questions.map((q, index) => (
                  <div key={q._key} className="flex gap-3">
                    <div className="flex-1">
                      <Label htmlFor={`question-${index}`}>問題 {index + 1} *</Label>
                      <Select value={q.selected} onValueChange={(v) => handleSelect(index, v)}>
                        <SelectTrigger className="rounded-sm mt-2" data-testid={`question-select-${index}`}>
                          <SelectValue placeholder="選擇一條問題，或自訂…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[60vh]">
                          <SelectItem value={CUSTOM_OPTION}>✍️ 自訂問題…</SelectItem>
                          <SelectSeparator />
                          {QUESTION_CATEGORIES.map((cat) => (
                            <SelectGroup key={cat.category}>
                              <SelectLabel className="text-[#C93B2B]">{cat.category}</SelectLabel>
                              {cat.questions.map((qt) => (
                                <SelectItem key={qt} value={qt}>{qt}</SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>

                      {q.selected === CUSTOM_OPTION && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1 text-xs text-[#5C5C5C] mb-1">
                            <Pencil className="w-3 h-3" strokeWidth={1.5} />
                            自訂問題（只詢問現在或過去）
                          </div>
                          <Textarea
                            value={q.customText}
                            onChange={(e) => handleCustom(index, e.target.value)}
                            placeholder="例：我小時候是否曾經有一場大病？"
                            className="rounded-sm"
                            rows={2}
                            maxLength={100}
                            data-testid={`question-custom-${index}`}
                          />
                          <p className="text-xs text-[#9A9A9A] mt-1 text-right">{q.customText.length}/100</p>
                        </div>
                      )}

                      <div className="mt-2 bg-[#F5F0E6] rounded-sm p-3">
                        <Label htmlFor={`truth-${index}`} className="text-xs text-[#5C5C5C] leading-relaxed">
                          真相（選填）——你已知道嘅真實答案，用嚟評核命理師準唔準。<b>唔會畀命理師睇</b>；一填就要<b>全部問題都填</b>，唔填住？提交後喺求測單頁都可以補填。
                        </Label>
                        <Textarea
                          id={`truth-${index}`}
                          value={q.truth}
                          onChange={(e) => handleTruth(index, e.target.value)}
                          placeholder="例：我 2023 年中轉咗一份工"
                          className="rounded-sm mt-1 bg-white"
                          rows={2}
                          maxLength={50}
                          data-testid={`question-truth-${index}`}
                        />
                      </div>
                    </div>
                    {questions.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveQuestion(index)}
                        className="mt-8 rounded-sm flex-shrink-0"
                        data-testid={`remove-question-${index}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {questions.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddQuestion}
                  className="mt-4 rounded-sm border-[#E5E0D8] hover:bg-[#F5F0E6]"
                  data-testid="add-question-btn"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  新增問題
                </Button>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm border-[#E5E0D8]"
                data-testid="cancel-btn"
                onClick={() => {
                  if (window.confirm('確定取消？已填嘅草稿會一併清除。')) {
                    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
                    navigate('/client/dashboard');
                  }
                }}
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
                data-testid="submit-request-btn"
              >
                {loading ? '提交中...' : '提交求測單'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRequest;
